import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Package, Layers, Droplets, ArrowLeft, Ruler } from "lucide-react";
import { Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import { getProduct } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

const SpecRow = ({ label, value }) => (
  <div className="flex flex-col gap-1 border-b border-black/10 py-5 md:flex-row md:items-start md:justify-between md:gap-8">
    <span className="label shrink-0 text-muted-foreground">{label}</span>
    <span className="text-right font-display text-base text-foreground md:max-w-md">{value}</span>
  </div>
);

export default function ProductDetail() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getProduct(slug)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-20" data-testid="product-loading">
        <span className="label animate-pulse text-muted-foreground">{t("productDetail.loading")}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 pt-20" data-testid="product-not-found">
        <SEO
          title="Product Not Found | Ashlar Buildchem"
          description="The requested construction chemical product could not be found."
          canonical={`/products/${slug}`}
        />
        <h1 className="font-display text-4xl">{t("productDetail.notFound")}</h1>
        <Link to="/products" className="label text-primary">{t("productDetail.back")}</Link>
      </div>
    );
  }

  const { product, related } = data;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "image": product.image?.startsWith("http")
      ? product.image
      : `https://www.ashlarbuildchem.com${product.image?.startsWith("/") ? "" : "/"}${product.image || "logo.png"}`,
    "description": product.description || product.tagline,
    "sku": product.code,
    "brand": {
      "@type": "Brand",
      "name": "Ashlar Buildchem"
    },
    "category": product.category_name,
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "INR",
      "url": `https://www.ashlarbuildchem.com/products/${product.slug}`
    }
  };

  return (
    <div className="pt-20" data-testid="product-detail-page">
      <SEO
        title={`${product.name} | Ashlar Buildchem`}
        description={product.description || product.tagline}
        keywords={`${product.name}, ${product.category_name}, Ashlar Buildchem, ${product.classification || ""}, tile adhesive, construction chemicals`}
        canonical={`/products/${product.slug}`}
        ogType="product"
        ogImage={product.image}
        schema={productSchema}
      />
      <div className="mx-auto max-w-[1600px] px-6 pb-24 pt-10 md:px-10">
        <Link to="/products" className="group inline-flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-to-products">
          <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
          <span className="label">{t("productDetail.catalogue")}</span>
        </Link>

        <div className="mt-10 grid gap-16 lg:grid-cols-[1fr_1.1fr]">
          {/* Sticky left */}
          <div className="lg:sticky lg:top-28 lg:h-fit">
            <span className="label text-primary">{product.code} · {product.category_name}</span>
            <h1 className="mt-4 font-display text-3xl font-bold leading-[0.9] tracking-tight md:text-7xl">
              {product.name}
            </h1>
            <p className="mt-6 max-w-md text-lg text-muted-foreground">{product.tagline}</p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-10 overflow-hidden bg-gradient-to-b from-neutral-100 to-neutral-300 p-10"
            >
              <img
                src={product.image}
                alt={product.name}
                className="mx-auto h-72 w-auto object-contain drop-shadow-2xl md:h-96"
              />
            </motion.div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="product-enquire"
                className="group inline-flex flex-1 items-center justify-center gap-2 bg-primary px-6 py-4 text-primary-foreground transition-colors duration-200 hover:bg-foreground hover:text-background"
              >
                <span className="label text-[0.7rem]">{t("productDetail.enquire")}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
              </Link>
              <Link
                to="/calculators"
                data-testid="product-calculator"
                className="inline-flex items-center justify-center gap-2 border border-black/15 px-6 py-4 hover:bg-black/5"
              >
                <Ruler className="h-4 w-4 text-primary" />
                <span className="label text-[0.7rem]">{t("productDetail.calculate")}</span>
              </Link>
            </div>
          </div>

          {/* Scrolling right */}
          <div>
            <Reveal>
              <span className="label text-muted-foreground">{t("productDetail.overview")}</span>
              <p className="mt-5 text-lg leading-relaxed text-foreground/85">{product.description}</p>
            </Reveal>

            {/* Quick facts */}
            <div className="mt-12 grid grid-cols-2 gap-px bg-black/10 md:grid-cols-3">
              {[
                { icon: Layers, label: t("productDetail.classLabel"), value: product.classification },
                { icon: Package, label: t("productDetail.packaging"), value: product.packaging[0] },
                { icon: Droplets, label: t("productDetail.colour"), value: product.color },
              ].map((f) => (
                <div key={f.label} className="bg-background p-6">
                  <f.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <div className="label mt-4 text-muted-foreground">{f.label}</div>
                  <div className="mt-1 font-display text-base">{f.value}</div>
                </div>
              ))}
            </div>

            {/* Features */}
            <Reveal className="mt-14">
              <h3 className="font-display text-2xl font-semibold">{t("productDetail.keyFeatures")}</h3>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 border border-black/10 p-4">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-sm text-foreground/85">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* Specs */}
            <Reveal className="mt-14">
              <h3 className="font-display text-2xl font-semibold">{t("productDetail.technicalData")}</h3>
              <div className="mt-6">
                <SpecRow label={t("productDetail.classification")} value={product.classification} />
                <SpecRow label={t("productDetail.coverage")} value={product.coverage} />
                <SpecRow label={t("productDetail.colour")} value={product.color} />
                <SpecRow label={t("productDetail.packaging")} value={product.packaging.join(" · ")} />
                <SpecRow label={t("productDetail.applications")} value={product.applications.join(", ")} />
                <SpecRow label={t("productDetail.substrates")} value={product.substrates.join(", ")} />
              </div>
            </Reveal>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-28">
            <div className="mb-10 flex items-end justify-between">
              <h3 className="font-display text-3xl font-semibold md:text-5xl">{t("productDetail.related")}</h3>
              <Link to="/products" className="label text-primary hover:underline">{t("productDetail.allArrow")}</Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.06}>
                  <ProductCard product={p} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
